import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, take } from 'rxjs';

import { HttpService } from '@shared/services';
import { environment } from 'src/environments/environment';
import { AgentRunThread, AiChatFileExportData, AiChatFileExportFormat } from './cipo-ai.component';

export interface AiChatContextSelection {
  restrictToCurrentContext?: boolean;
  programEntityInstanceId?: number;
  projectEntityInstanceId?: number;
  contractEntityInstanceId?: number;
  entityInstanceId?: number;
  projectFolderId?: number;
}

export type AiChatTelemetryEventType = 'completed' | 'completed_after_soft_timeout';

export interface AiChatTelemetryEvent {
  eventType: AiChatTelemetryEventType;
  threadId?: string;
  elapsedSeconds?: number;
  timeoutSeconds?: number;
  softTimeoutsShownCount?: number;
  keepWaitingCount?: number;
}

export interface AiChatFileExportRequest extends AiChatFileExportData {
  threadId?: string;
  format: AiChatFileExportFormat;
}

@Injectable({ providedIn: 'root' })
export class CipoAiService {
  private http = inject(HttpService);
  private httpClient = inject(HttpClient);

  askQuestion(threadId: string, text: string, contextSelection?: AiChatContextSelection): Observable<AgentRunThread> {
    return this.http.post<AgentRunThread>(
      `_api/ai-chat/ask-question`,
      { threadId, text, contextSelection },
      { useLoader: false },
    );
  }

  recordTelemetry(event: AiChatTelemetryEvent): Observable<void> {
    return this.http.post<void>(`_api/ai-chat/telemetry`, event, {
      useLoader: false,
      useErrorNotification: false,
    });
  }

  downloadExportFile(request: AiChatFileExportRequest): Observable<Blob> {
    const baseUrl = (environment.baseApiUrl ?? '').replace(/\/$/, '');
    return this.httpClient.post(`${baseUrl}/_api/ai-chat/download`, request, { responseType: 'blob' }).pipe(take(1));
  }

  getThreads(): Observable<AgentRunThread[]> {
    return this.http.get<AgentRunThread[]>(`_api/ai-chat/threads`, { useLoader: false });
  }

  getThread(threadId: string): Observable<AgentRunThread> {
    return this.http.get<AgentRunThread>(`_api/ai-chat/threads/${threadId}`, { useLoader: false });
  }

  deleteThread(threadId: string): Observable<AgentRunThread> {
    return this.http.delete<AgentRunThread>(`_api/ai-chat/threads/${threadId}/delete`, { useLoader: false });
  }
}
